import { Dispatch, ReactNode, SetStateAction } from 'react';
import { ButtonColor, Theme, ValueOf } from '@redsift/design-system';
import { useDialog } from './useDialog';

/**
 * Context props.
 */
export type DialogState =
  | (ReturnType<typeof useDialog> & {
      setLabelId: Dispatch<SetStateAction<string | undefined>>;
      setDescriptionId: Dispatch<SetStateAction<string | undefined>>;
      /** Class name to append to the trigger. */
      readonly triggerClassName?: string;
    })
  | null;

/**
 * Component variant.
 */
export const DialogSize = {
  small: 'small',
  medium: 'medium',
  large: 'large',
  xlarge: 'xlarge',
} as const;
export type DialogSize = ValueOf<typeof DialogSize>;

/**
 * Component props.
 */
export interface DialogProps {
  /** Button color that will be forward to the trigger. */
  color?: ButtonColor;
  /** Children. Can only be DialogTrigger and DialogContent. */
  children: ReactNode;
  /**
   * Default open status.
   * Used for uncontrolled version.
   */
  defaultOpen?: boolean;
  /**
   * Whether the Close icon button is displayed or not.
   * @default true
   */
  hasCloseButton?: boolean;
  /** Which element to initially focus. Can be either a number (tabbable index), a ref to en element, or a shortcut pointing towards a section of the dialog. See the accessibility section in the documentation to know which one to use. */
  initialFocus?: number | React.MutableRefObject<HTMLElement | null> | 'header' | 'body' | 'actions';
  /**
   * Whether the component is opened or not.
   * Used for controlled version.
   */
  isOpen?: boolean;
  /** Method to handle component change. */
  onOpen?: (open: boolean) => void;
  /**
   * Dialog size.
   * @default 'medium'
   */
  size?: DialogSize | { width?: string; maxWidth?: string; minWidth?: string };
  /** Theme. */
  theme?: Theme;
  /** Class name to append to the trigger. */
  triggerClassName?: string;
}

export type StyledDialogProps = DialogProps;
